'use client';

import { useEffect, useRef } from 'react';
import { apiClient } from '@/lib/api-client';

interface WindowWithErrors extends Window {
  __recentConsoleErrors?: string[];
}

const MAX_REPORTS_PER_MINUTE = 20;
const MAX_RECENT_ERRORS = 10;

/**
 * Global error reporter — captures:
 * 1. window.onerror (JS runtime errors)
 * 2. unhandledrejection (Promise rejections)
 * 3. console.error() calls (API 500s, React warnings, etc.)
 * Sends them to POST /error-logs for platform-wide error tracking.
 */
export function useErrorReporter() {
  const reportCount = useRef(0);
  const lastReset = useRef(Date.now());
  const recentErrors = useRef(new Set<string>());

  useEffect(() => {
    function canReport(key: string): boolean {
      const now = Date.now();
      if (now - lastReset.current > 60000) {
        reportCount.current = 0;
        lastReset.current = now;
        recentErrors.current.clear();
      }
      if (reportCount.current >= MAX_REPORTS_PER_MINUTE) return false;
      if (recentErrors.current.has(key)) return false;
      recentErrors.current.add(key);
      reportCount.current++;
      return true;
    }

    function sendReport(data: {
      message: string;
      stack?: string;
      component?: string;
      severity?: string;
      metadata?: Record<string, unknown>;
    }) {
      const key = `${data.message}::${data.component || ''}`;
      if (!canReport(key)) return;

      apiClient.post('/error-logs', {
        ...data,
        url: window.location.href,
        userAgent: navigator.userAgent,
      }).catch(() => {
        // error-logs endpoint unavailable — error report dropped silently
      });
    }

    // 1. Capture unhandled JS errors
    function handleError(event: ErrorEvent) {
      sendReport({
        message: event.message || 'Unknown error',
        stack: event.error?.stack,
        component: event.filename ? `${event.filename}:${event.lineno}:${event.colno}` : undefined,
        severity: 'error',
      });
    }

    // 2. Capture unhandled promise rejections
    function handleRejection(event: PromiseRejectionEvent) {
      const reason = event.reason;
      const message = reason instanceof Error ? reason.message : String(reason || 'Unhandled promise rejection');
      const stack = reason instanceof Error ? reason.stack : undefined;
      if (message.includes('401') || message.includes('403') || message.includes('Unauthorized')) return;
      sendReport({ message, stack, severity: 'error', metadata: { type: 'unhandledrejection' } });
    }

    // 3. Intercept console.error — captures API 500s, React warnings, etc.
    const originalConsoleError = console.error;
    const originalConsoleWarn = console.warn;

    console.error = function (...args: unknown[]) {
      originalConsoleError.apply(console, args);

      // Store recent errors for the feedback button to include
      if (typeof window !== 'undefined') {
        const w = window as WindowWithErrors;
        if (!w.__recentConsoleErrors) w.__recentConsoleErrors = [];
        const errMsg = args.map(a => typeof a === 'string' ? a : (a instanceof Error ? a.message : String(a))).join(' ').substring(0, 200);
        if (errMsg && !errMsg.includes('error-logs')) {
          w.__recentConsoleErrors.push(errMsg);
          if (w.__recentConsoleErrors.length > MAX_RECENT_ERRORS) {
            w.__recentConsoleErrors.shift();
          }
        }
      }

      // Build message from arguments
      const parts = args.map(a => {
        if (a instanceof Error) return a.message;
        if (typeof a === 'string') return a;
        try { return JSON.stringify(a)?.substring(0, 500); } catch { return String(a); }
      });
      const message = parts.join(' ').substring(0, 2000);

      // Skip noise
      if (!message) return;
      if (message.includes('error-logs')) return; // don't report errors about error reporting
      if (message.includes('401') || message.includes('Unauthorized')) return;
      if (message.includes('hydration') || message.includes('Hydration')) return; // React dev noise
      if (message.includes('Warning:')) return; // React dev warnings

      const stack = args.find(a => a instanceof Error)
        ? (args.find(a => a instanceof Error) as Error).stack
        : undefined;

      sendReport({
        message,
        stack,
        severity: 'error',
        metadata: { type: 'console.error' },
      });
    };

    // Also capture console.warn for API warnings (optional — lower severity)
    console.warn = function (...args: unknown[]) {
      originalConsoleWarn.apply(console, args);

      const parts = args.map(a => typeof a === 'string' ? a : String(a));
      const message = parts.join(' ').substring(0, 1000);

      if (!message) return;
      if (message.includes('error-logs')) return;
      if (message.includes('Warning:')) return; // React dev warnings — too noisy
      if (message.includes('DevTools')) return;

      // Only report warnings that look like real problems (500, failed, error)
      if (message.match(/\b(500|failed|error|eroare|esuat)\b/i)) {
        sendReport({
          message,
          severity: 'warning',
          metadata: { type: 'console.warn' },
        });
      }
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
      console.error = originalConsoleError;
      console.warn = originalConsoleWarn;
    };
  }, []);
}

/**
 * Report an error programmatically (for use in catch blocks, ErrorBoundary, etc.)
 */
export function reportError(error: Error | string, extra?: { component?: string; severity?: string; metadata?: Record<string, unknown> }) {
  const message = error instanceof Error ? error.message : error;
  const stack = error instanceof Error ? error.stack : undefined;

  apiClient.post('/error-logs', {
    message,
    stack,
    url: typeof window !== 'undefined' ? window.location.href : undefined,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    ...extra,
  }).catch(() => {
    // error-logs endpoint unavailable — error report dropped silently
  });
}
